import React, { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  ScrollText, Loader2, ShieldAlert, AlertTriangle, Download, Sparkles, TrendingUp, TrendingDown, Minus,
} from "lucide-react";
import { toast } from "sonner";

const PRIORITY_STYLES = {
  "High":         { color: "bg-red-100 text-red-800 border-red-200",          dot: "bg-red-500" },
  "Medium-High":  { color: "bg-orange-100 text-orange-800 border-orange-200", dot: "bg-orange-500" },
  "Medium":       { color: "bg-amber-50 text-amber-800 border-amber-200",     dot: "bg-amber-500" },
  "Low-Medium":   { color: "bg-blue-50 text-blue-800 border-blue-200",        dot: "bg-blue-500" },
  "Awareness":    { color: "bg-slate-100 text-slate-700 border-slate-200",    dot: "bg-slate-400" },
};
const TYPE_STYLES = {
  "Legal":       "bg-purple-50 text-purple-800 border-purple-200",
  "Financial":   "bg-emerald-50 text-emerald-800 border-emerald-200",
  "Operational": "bg-cyan-50 text-cyan-800 border-cyan-200",
};

/**
 * Embeds a contract review for a given negotiation_id.
 * Auto-polls the latest job for this negotiation, then renders the 5-column table.
 */
export default function ContractReviewPanel({ negotiationId, initialJobId = null }) {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jobStatus, setJobStatus] = useState(null); // 'running' | 'done' | 'error' | null
  const [jobError, setJobError] = useState(null);
  const pollTimer = useRef(null);

  const fetchReview = async () => {
    try {
      const r = await api.get(`/contract-reviews/by-negotiation/${negotiationId}`);
      setReview(r.data?.review || null);
    } catch (e) {
      setReview(null);
    }
  };

  // Poll the job until done; fall back to fetching the most recent review
  const pollJob = async (jobId) => {
    try {
      const r = await api.get(`/contract-review/jobs/${jobId}`);
      const job = r.data;
      setJobStatus(job?.status);
      if (job?.status === "done") {
        clearInterval(pollTimer.current);
        await fetchReview();
        setLoading(false);
      } else if (job?.status === "error") {
        clearInterval(pollTimer.current);
        setJobError(job?.error || "Review failed");
        setLoading(false);
      }
    } catch (e) {
      // 404 = expired; stop polling
      clearInterval(pollTimer.current);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!negotiationId) return;
    setLoading(true);
    setJobError(null);

    // Look for an existing review immediately
    fetchReview().then(() => {
      // If there's a known in-flight job id, start polling it
      if (initialJobId) {
        setJobStatus("running");
        pollJob(initialJobId); // immediate
        pollTimer.current = setInterval(() => pollJob(initialJobId), 3000);
      } else {
        setLoading(false);
      }
    });

    return () => clearInterval(pollTimer.current);
  }, [negotiationId, initialJobId]);

  const rerun = async (file) => {
    setLoading(true);
    setJobError(null);
    const fd = new FormData();
    fd.append("file", file);
    // also include negotiation_id so the review is linked
    fd.append("cta_id", ""); // intentionally empty
    try {
      // We don't have an endpoint that takes negotiation_id directly for upload,
      // so we just upload and rely on the user's pick.
      // For now, fallback to api.post via fetch is simpler — but to keep this slim,
      // we hit the existing /contract-review/start which doesn't link by negotiation.
      // Instead, advise re-uploading via Negotiation list.
      toast.info("Re-run currently only available by re-creating the negotiation. Coming soon.");
    } finally {
      setLoading(false);
    }
  };

  const exportDocx = async () => {
    if (!review?.id) return;
    try {
      const r = await api.get(`/contract-reviews/${review.id}/export.docx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const safe = (review.study_name || review.source_filename || "contract_review").replace(/\s+/g, "_");
      a.download = `${safe}_review.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to export");
    }
  };

  if (loading || (jobStatus === "running" && !review)) {
    return (
      <div className="bg-white border border-border rounded-md p-10 text-center" data-testid="cr-panel-loading">
        <Loader2 className="w-7 h-7 mx-auto animate-spin text-[#0A4C95]" />
        <div className="mt-3 font-semibold text-slate-900 text-sm">AI Contract Language Review in progress…</div>
        <div className="text-xs text-slate-500 mt-1">Claude is reviewing every clause against the 23-category Stryde checklist. Usually 30–90 seconds.</div>
      </div>
    );
  }

  if (jobError) {
    return (
      <div className="bg-white border border-red-200 rounded-md p-6" data-testid="cr-panel-error">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-slate-900">Review failed</div>
            <div className="text-sm text-slate-600 mt-1">{jobError}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!review) {
    return (
      <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center" data-testid="cr-panel-empty">
        <ScrollText className="w-12 h-12 mx-auto text-slate-300" />
        <h4 className="mt-3 font-semibold text-slate-900">No contract review yet</h4>
        <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
          A contract language review is auto-generated when you upload a sponsor draft via the Negotiation Worksheet. If the upload predates this feature, re-upload to generate one.
        </p>
      </div>
    );
  }

  const counts = review.counts || {};
  const missing = review.missing_categories || [];
  const rows = review.rows || [];

  return (
    <div className="space-y-5" data-testid="cr-panel">
      {/* Header */}
      <div className="bg-white border border-border rounded-md shadow-sm p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-semibold text-slate-900 inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#F05D23]" /> Contract Language Review
            </h3>
            <div className="text-xs text-slate-500 mt-0.5">
              {review.source_filename && <span className="font-mono">{review.source_filename}</span>}
              <span className="ml-2">Generated {(review.created_at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={exportDocx} data-testid="cr-panel-export">
            <Download className="w-3.5 h-3.5 mr-1" /> Word
          </Button>
        </div>
        {review.summary && (
          <div className="mt-3 rounded-md border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-700 leading-relaxed">
            {review.summary}
          </div>
        )}
        {/* Counts strip */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-6 gap-2">
          <CountChip label="Total" value={counts.total || 0} dot="bg-slate-500" />
          <CountChip label="Escalate" value={counts.escalate || 0} dot="bg-red-500" highlight />
          <CountChip label="High" value={counts.High || 0} dot="bg-red-500" />
          <CountChip label="Med-High" value={counts["Medium-High"] || 0} dot="bg-orange-500" />
          <CountChip label="Medium" value={counts.Medium || 0} dot="bg-amber-500" />
          <CountChip label="Awareness" value={counts.Awareness || 0} dot="bg-slate-400" />
        </div>
      </div>

      {/* Main table */}
      <div className="bg-white border border-border rounded-md shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-border bg-slate-50/40 flex items-center justify-between">
          <h3 className="font-semibold text-sm text-slate-900">Clause-by-Clause Review</h3>
          <span className="text-xs text-slate-500">{rows.length} clause{rows.length === 1 ? "" : "s"} flagged</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/70 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 w-16">Sr. No.</th>
                <th className="text-left px-3 py-2 w-[28%]">Section Provision</th>
                <th className="text-left px-3 py-2 w-[26%]">Risk / Issue</th>
                <th className="text-left px-3 py-2 w-32">Priority</th>
                <th className="text-left px-3 py-2">Suggested Negotiation</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500 text-sm">No risky clauses found — contract looks clean.</td></tr>
              )}
              {rows.map((row, i) => {
                const pStyle = PRIORITY_STYLES[row.priority] || PRIORITY_STYLES.Medium;
                const tStyle = TYPE_STYLES[row.type] || TYPE_STYLES.Legal;
                return (
                  <tr key={i} className={`border-t border-slate-100 ${row.escalate ? "bg-red-50/30" : "hover:bg-slate-50/40"}`} data-testid={`cr-panel-clause-${i}`}>
                    <td className="px-3 py-3 align-top text-xs font-mono text-slate-700">{row.sr_no || (i + 1)}</td>
                    <td className="px-3 py-3 align-top">
                      <Badge variant="outline" className={`${tStyle} text-[10px] mb-1.5`}>{row.category}</Badge>
                      <div className="text-[12.5px] text-slate-700 leading-snug">{row.section_provision}</div>
                    </td>
                    <td className="px-3 py-3 align-top text-[12.5px] text-slate-600 leading-snug">{row.risk_issue}</td>
                    <td className="px-3 py-3 align-top">
                      <Badge variant="outline" className={`${pStyle.color} inline-flex items-center gap-1`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${pStyle.dot}`}></span>
                        {row.priority}
                      </Badge>
                      {row.escalate && (
                        <div className="mt-1.5 inline-flex items-center gap-1 text-[10.5px] text-red-700 font-semibold">
                          <ShieldAlert className="w-3 h-3" /> Escalate
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 align-top text-[12.5px] text-slate-700 leading-snug">{row.suggested_negotiation}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {missing.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-md p-5" data-testid="cr-panel-missing">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-slate-900 text-sm">Checklist Categories Not Addressed</h4>
              <p className="text-xs text-slate-600 mt-1">Consider asking sponsor to add explicit language:</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {missing.map((m) => (
                  <Badge key={m} variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">{m}</Badge>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CountChip({ label, value, dot, highlight }) {
  return (
    <div className={`border rounded-md px-3 py-2 ${highlight ? "border-red-200 bg-red-50/40" : "border-slate-200 bg-white"}`}>
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
        <span className={`w-1.5 h-1.5 rounded-full ${dot}`}></span>{label}
      </div>
      <div className={`font-mono-fin text-lg font-semibold mt-0.5 ${highlight && value > 0 ? "text-red-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

// keep these imports tree-shake-friendly
export { TrendingUp, TrendingDown, Minus };
